/* eslint relay/unused-fields: "off" */

import { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay/legacy';
import { connect } from 'react-redux';
import { IntlContext } from 'dibs-react-intl';
import { datadogRum } from '@datadog/browser-rum';

// components
import { SharedUserAndRelayVarLoader } from '../shared/SharedUserAndRelayVarLoader';
import { CurrencyProvider } from 'dibs-buyer-layout/exports/CurrencyProvider';
import { ServerVarsContextConsumer } from '../global/ServerVarsContext/ServerVarsContext';
import { PdpHead } from './helpers/PdpHeadBuilder/PdpHeadDeprecated';
import { SharedTrackingContainer } from '../shared/SharedTrackingContainer';
import { BrowserHistoryContextProvider } from '../global/BrowserHistoryContext/BrowserHistoryContext';
import { getSkuTrackingParams } from './tracking/helpers/getSkuTrackingParams';
import { getPrivateOffer } from './helpers/getPrivateOffer';
import { handleLocaleUrl, GLOBAL_CLIENT_ONLY_LOCALE } from 'dibs-intl/exports/urls';

// helpers
import get from 'lodash.get';
import { getPriceStatus } from '../pdp/PdpSharedPricingGuidance/pricingGuidanceTracking';
import { trackEcommerce } from './tracking/pdpTracking';
import { getLocalHistoryItemIds } from 'dibs-recent-history/exports/getLocalHistoryItemIds';
import { MAX_CAROUSEL_RECENTLY_VIEWED_ITEMS } from 'dibs-recent-history/exports/constants';
import {
    sendChatMessageOnPageRefetch,
    setHasPageRefetched,
} from 'dibs-buyer-layout/exports/loginHandlers';
import { setDwelledOnPdp } from 'dibs-buyer-layout/exports/dwelledOnPdpHelpers';
import { PdpSharedOriginalLanguageProvider } from './PdpSharedOriginalLanguageProvider/PdpSharedOriginalLanguageProvider';
import { useBuyerUrgency } from 'dibs-buyer-urgency/exports/useBuyerUrgency';
import { getFdfQuoteABTestVariant } from 'dibs-buyer-layout/src/helpers/getFdfQuoteABTestVariant';
import { pdpMetaTypes } from '../constants/pageTypeConstants';
import { handlePageRefetchVariableOnUserChange } from '../shared/handlePageRefetchVariableOnUserChange';
import { addToEngagedItems } from 'dibs-buyer-layout/exports/engagedItems';

const ADD_TO_ENGAGED_ITEMS_AFTER_MS = 8000;
export class PdpSharedLayoutComponent extends Component {
    constructor(props) {
        super(props);

        this.getFollowSearchData = this.getFollowSearchData.bind(this);
        this.handleUserRefetch = this.handleUserRefetch.bind(this);
        this.trackingFunction = this.trackingFunction.bind(this);
        this.onSkuChange = this.onSkuChange.bind(this);
        this.getSkuWithId = this.getSkuWithId.bind(this);
        this.state = { skuId: null, numberOfSkuImages: null };
    }

    componentDidMount() {
        this.timeoutId = setDwelledOnPdp();
        const itemId = this.props.viewer?.itemRead?.[0]?.serviceId;

        this.addToEngagedItemsTimeoutId = setTimeout(
            addToEngagedItems,
            ADD_TO_ENGAGED_ITEMS_AFTER_MS,
            itemId
        );
    }

    componentDidUpdate(prevProps, prevState) {
        // if msku, determine which sku to land on
        // prioritize sku with PO if one exists for buyer
        // or use skuId in url if already specified
        // finally fall back to item's default sku id
        if (!this.state.skuId) {
            const privateOffer = this.isMultiSku()
                ? getPrivateOffer(this.props?.viewer?.transactions?.edges)
                : null;
            const privateOfferSkuId = privateOffer?.skuId || null;
            const defaultSkuId = this.props?.viewer?.itemRead?.[0]?.defaultSkuId;
            // get sku id from url hash when component mounts
            const hash = window.location.hash || '';
            const skuIdFromUrl = get(hash.match(/skuId=(\w+)/), '1');
            const skuIdFromUrlIsValid = skuIdFromUrl && !!this.getSkuWithId(skuIdFromUrl);
            const initialSkuId = skuIdFromUrlIsValid
                ? skuIdFromUrl
                : privateOfferSkuId || defaultSkuId;
            this.onSkuChange(initialSkuId);
        }

        // update url once state has been updated with proper initial sku
        if (this.isMultiSku() && this.state.skuId !== prevState.skuId) {
            const queryParams = window.location.search || '';
            // update hash when sku changes
            window.history.replaceState(
                null,
                null,
                handleLocaleUrl(
                    `${window.location.pathname}${queryParams}#skuId=${this.state.skuId}`,
                    GLOBAL_CLIENT_ONLY_LOCALE
                )
            );
        }
    }

    componentWillUnmount() {
        clearTimeout(this.timeoutId);
        clearTimeout(this.addToEngagedItemsTimeoutId);
    }

    getSkuWithId(skuId) {
        const skus = get(this.props, 'viewer.itemRead.0.skus') || [];
        return skus.find(val => val.skuId === skuId);
    }

    getFollowSearchData() {
        const searchPage = this.props?.viewer?.itemRead?.[0]?.saveSearchData?.page || '';
        return { followSearchPages: [searchPage] };
    }

    isMultiSku() {
        return !!get(this.props, 'viewer.itemRead.0.isMultiSku');
    }

    handleUserRefetch(variables, renderVariables, callback) {
        const localRecentHistoryItemIds = getLocalHistoryItemIds({ isClient: variables.isClient });
        const { viewer } = this.props;
        const item = viewer?.itemRead?.[0];
        const isAuctionEnabled = item?.isAuctionEnabled;
        const isSellerAccessible = item?.seller?.isAccessible;
        const fdfQuoteABTestVariant = getFdfQuoteABTestVariant();
        const fetchPdp = handlePageRefetchVariableOnUserChange(variables);

        const refetchVariables = {
            ...variables,
            ...this.getFollowSearchData(),
            fetchPdp,
            isMultiSku: this.isMultiSku(),
            localRecentHistoryItemIds,
            fetchUserRecentHistoryItem:
                localRecentHistoryItemIds.length < MAX_CAROUSEL_RECENTLY_VIEWED_ITEMS,
            isInteractivePdpChat: !!isSellerAccessible, // enable chat if seller is accessible
            timezoneOffset: new Date().getTimezoneOffset(),
            fetchAuction: !!isAuctionEnabled,
            fetchAuctionOrBuaSsr: !!isAuctionEnabled,
            fdfQuoteABTestVariant,
        };
        const updatedRenderVariables = {
            ...refetchVariables,
            ...renderVariables,
            fetchPdp: true,
        };
        this.props.doRefetch(refetchVariables, updatedRenderVariables, err => {
            if (!err) {
                setHasPageRefetched();
                sendChatMessageOnPageRefetch();
                if (datadogRum.getInternalContext()) {
                    // datadog rum is initialized
                    datadogRum.addTiming('pdp_time_to_refetch');
                }
            }
            callback(err, updatedRenderVariables);
        });
    }

    getTrackingData() {
        const item = this.props.viewer?.itemRead?.[0];
        const soldPriceRange = getPriceStatus({ item });

        const params = get(this.props, 'viewer.itemRead.0.pdpTrackingVpvParams') || {};
        const { additionalTrackingData } = this.props;
        const skuParams = {
            skuType: this.isMultiSku() ? 'multi' : 'single',
            skuId: this.state.skuId,
            numberOfSkuImages: this.state.numberOfSkuImages,
            ...(item?.isMultiSku && getSkuTrackingParams(params, this.state.skuId)),
        };
        const buyerDisplayablePromo = item?.activePromotion?.code;

        return {
            ...params,
            ...additionalTrackingData,
            ...skuParams,
            soldPriceRange,
            hasViewInRoom: get(this.props, 'viewer.itemRead.0.viewInRoomPhotos', []).length,
            buyerUrgency: !!this.props.showBuyerUrgencyBanner,
            buyerDisplayablePromo,
        };
    }

    trackingFunction() {
        const item = get(this.props, 'viewer.itemRead.0');
        trackEcommerce(item, this.state.skuId);
    }

    onSkuChange(skuId) {
        if (skuId !== this.state.skuId) {
            const sku = this.getSkuWithId(skuId);
            const numberOfSkuImages = get(sku, 'images.length');

            this.setState({
                skuId,
                numberOfSkuImages,
            });
        }
    }

    render() {
        const { user, refreshPageOnTradeLogin } = this.props;
        const { url: opt_pageURL, ...additional } = this.getTrackingData() || {}; // eslint-disable-line camelcase
        const gtmPageInfo = { opt_pageURL }; // eslint-disable-line camelcase
        const onSkuChange = this.onSkuChange;

        return (
            <BrowserHistoryContextProvider>
                <ServerVarsContextConsumer>
                    {({ clientConf, originalUrl }) => {
                        const pdpHead = new PdpHead({
                            ...this.props,
                            intl: this.context,
                            clientConf,
                            originalUrl,
                            pdpMetaType: pdpMetaTypes.available,
                        });
                        return (
                            <PdpSharedOriginalLanguageProvider
                                item={this.props.viewer?.itemRead?.[0]}
                            >
                                <SharedUserAndRelayVarLoader
                                    refreshPageOnTradeLogin={refreshPageOnTradeLogin}
                                    onUserRefetch={this.handleUserRefetch}
                                />
                                <CurrencyProvider
                                    render={({ currency, showCurrency }) => {
                                        return (
                                            <Fragment>
                                                <SharedTrackingContainer
                                                    user={user}
                                                    gtmPageInfo={gtmPageInfo}
                                                    onTrackingFired={this.trackingFunction}
                                                    additional={additional}
                                                    skuId={this.state.skuId}
                                                    isMultiSku={this.isMultiSku()}
                                                />
                                                {this.props.children({
                                                    pdpHead,
                                                    currency,
                                                    showCurrency,
                                                    selectedSkuId: this.state.skuId,
                                                    onSkuChange,
                                                })}
                                            </Fragment>
                                        );
                                    }}
                                />
                            </PdpSharedOriginalLanguageProvider>
                        );
                    }}
                </ServerVarsContextConsumer>
            </BrowserHistoryContextProvider>
        );
    }
}

PdpSharedLayoutComponent.contextType = IntlContext;

PdpSharedLayoutComponent.defaultProps = {
    additionalTrackingData: {},
};

PdpSharedLayoutComponent.propTypes = {
    children: PropTypes.func.isRequired,
    additionalTrackingData: PropTypes.object,
    doRefetch: PropTypes.func.isRequired,
    viewer: PropTypes.object.isRequired,
    user: PropTypes.object,
    refreshPageOnTradeLogin: PropTypes.bool,
    showBuyerUrgencyBanner: PropTypes.bool,
};

function mapStateToProps({ intlVariables }) {
    return { intlVariables };
}

const withHooksHOC = Comp => {
    const Wrapper = props => {
        const { showBuyerUrgencyBanner } = useBuyerUrgency({ viewer: props.viewer });

        return <Comp {...props} showBuyerUrgencyBanner={showBuyerUrgencyBanner} />;
    };

    Wrapper.propTypes = PdpSharedLayoutComponent.propTypes;

    return Wrapper;
};

export const PdpSharedLayout = createFragmentContainer(
    connect(mapStateToProps)(withHooksHOC(PdpSharedLayoutComponent)),
    {
        viewer: graphql`
            fragment PdpSharedLayout_viewer on Viewer {
                ...PdpHeadDeprecated_viewer @relay(mask: false)
                itemRead(itemIds: $itemIds) {
                    ... on Item {
                        ...PdpSharedOriginalLanguageProvider_item
                        ...pricingGuidanceTracking_item
                        serviceId
                        isMultiSku
                        defaultSkuId
                        isAuctionEnabled
                        pdpTrackingVpvParams(
                            userId: $userId
                            userCountryCode: $userCountryCode
                            userZipCode: $userZipCode
                            fdfQuoteABTestVariant: $fdfQuoteABTestVariant
                        ) @include(if: $isClient)
                        saveSearchData {
                            page
                        }
                        ... @include(if: $fetchPdp) {
                            ...PdpHeadDeprecated_item @relay(mask: false)
                            pdpMeta {
                                title
                            }
                            attributes {
                                period {
                                    option {
                                        name
                                    }
                                }
                            }
                            browseUrl
                            categoryCode
                            seller {
                                serviceId
                                isInternal
                                isAccessible
                            }
                            viewInRoomPhotos {
                                src
                            }
                            contemporaryTrackingString
                        }
                        skus {
                            skuId
                            images {
                                id
                            }
                        }
                        activePromotion(userId: $userId) @include(if: $isClient) {
                            code
                        }
                    }
                }
                transactions(itemId: $itemIds, buyerId: $userIds) @include(if: $isClient) {
                    edges @include(if: $isMultiSku) {
                        node {
                            skuId
                            isPrivateOffer
                            isCanceled
                        }
                    }
                }
                ...useBuyerUrgency_viewer @arguments(itemId: $itemId, userId: $userId)
            }
        `,
        user: graphql`
            fragment PdpSharedLayout_user on User {
                ...SharedTrackingContainer_user
            }
        `,
    }
);
